import { Component, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";

import { Observable, Subscription } from "rxjs";
import { select, Store } from "@ngrx/store";
import {
  getFuelingModes,
  getFuelingModesExcel,
  getFuelingModesTableConfig,
  resetFuelingModesState,
  saveFuelingModesTableConfig,
} from "src/app/store/actions/fueling-modes/fueling-modes.actions";
import {
  AppState,
  fuelingModeExcelListState,
  fuelingModeIsLoading,
  fuelingModeListState,
  fuelingModeTableConfigState,
  fuelingModeTotalState,
  loadedMenusState,
} from "src/app/store/reducers";

import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  DefaultPage,
  ExportEvent,
  FieldType,
  FilterEvent,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { FuelingModeDTO } from "src/app/models/fueling-mode/fueling-modeDTO";
import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";
import { subMenusIterator } from "src/app/shared/utils/menuUtils";

const USAGE_CODE = "FUELING_MODES";

@Component({
  selector: "app-fueling-mode-list",
  templateUrl: "./fueling-mode-list.component.html",
  styleUrls: ["./fueling-mode-list.component.scss"],
})
export class FuelingModeListComponent implements OnInit, OnDestroy {
  isLoading$: Observable<boolean>;
  fuelingModeList$: Observable<FuelingModeDTO[]>;
  excelFuelingModeList$: Observable<FuelingModeDTO[]>;
  totalFuelingModes$: Observable<number>;

  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  formPath: string = "fueling-modes/fueling-mode";
  tableTitle: string;
  headerIcon: string;

  columnInfo$: Observable<ColumnSorterInfo[]>;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "FuelingMode.code",
      field: "code",
      fieldType: FieldType.STRING,
      identifier: true,
      hidden: true,
    },
    {
      header: "FuelingMode.enabled",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "FuelingMode.description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "FuelingMode.maxTimeToReachminLimit",
      field: "maxTimeToReachMinLimit",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "FuelingMode.maxTimeWithoutProgress",
      field: "maxTimeWithoutProgress",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "FuelingMode.maxTransactionVolume",
      field: "maxTransactionVolume",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "FuelingMode.maxFuellingTime",
      field: "maxFuellingTime",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "FuelingMode.maxTransactionAmount",
      field: "maxTransactionAmount",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "FuelingMode.maxVolumeOverrun",
      field: "maxVolumeOverrun",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "FuelingMode.clearDisplayTime",
      field: "clearDisplayTime",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "FuelingMode.clearDisplayWhenTransactionIsCleared",
      field: "clearDisplayWhenTransactionIsCleared",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "FuelingMode.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATETIME,
    },
    {
      header: "FuelingMode.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATETIME,
    },
  ];

  constructor(
    private store: Store<AppState>,
    private titleService: Title,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.isLoading$ = this.store.pipe(select(fuelingModeIsLoading));
    this.fuelingModeList$ = this.store.pipe(select(fuelingModeListState));
    this.excelFuelingModeList$ = this.store.pipe(
      select(fuelingModeExcelListState)
    );
    this.totalFuelingModes$ = this.store.pipe(select(fuelingModeTotalState));
    this.menus$ = this.store.pipe(select(loadedMenusState));
    this.columnInfo$ = this.store.pipe(select(fuelingModeTableConfigState));

    this.loadScreenTitle();
    this.getTableConfig();
    this.getFuelingModesList();
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    });
  }

  getFuelingModesList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getFuelingModes({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: false,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  getFuelingModeExcel(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getFuelingModesExcel({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getFuelingModesList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onGetExportData(exportEvent: ExportEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    this.getFuelingModeExcel(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  getTableConfig() {
    this.store.dispatch(
      getFuelingModesTableConfig({
        options: {
          code: USAGE_CODE,
          settingType: ConfigSettingType.TABLE_CONFIG,
        },
      })
    );
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.store.dispatch(
      saveFuelingModesTableConfig({
        config: {
          code: USAGE_CODE,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }
  
  ngOnDestroy(): void {
    this.store.dispatch(resetFuelingModesState());
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
  }
}
